import { hasPermission } from '../permissions/usePermissions';
import React, { useState, useEffect } from "react";
import { Card, Row, Col, Spin, Result, Button } from "antd";
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { useSystemNow } from '../hooks/useSystemNow';

// Icons import
import {
  UserOutlined,
  ShoppingCartOutlined,
  BankOutlined,
  BoxPlotOutlined,
  FileTextOutlined,
  FileDoneOutlined,
  FileExcelOutlined,
  DollarOutlined,
  TeamOutlined,
  RetweetOutlined,
  DashboardOutlined,
  LoadingOutlined,
  TruckOutlined
} from '@ant-design/icons';

const Home = () => {
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(false);
  const user = useSelector(state => state.user);
  const token = useSelector(state => state.token);
  const viewMode = useSelector(state => state.viewMode || 'vente');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const API_URL = `${process.env.REACT_APP_API_BASE_URL}`;
  
  // Call hooks at the top level, before any conditional returns
  const { now } = useSystemNow({ refreshMs: 20000 });

  const isManager = user?.IsManager;
  const canAccess = permission => Array.isArray(permission) ? permission.some(id => hasPermission(user, id)) : hasPermission(user, permission);

  // Authentication verification on component mount
  useEffect(() => {
    const verifyAuth = async () => {
      if (!token) {
        // No token in Redux store
        setAuthError(true);
        setLoading(false);
        return;
      }

      try {
        // Call any protected endpoint - your middleware will handle the auth check
        // Choose a lightweight endpoint that returns minimal data
        await axios.get(`${API_URL}/account/verify`, {  // Or any other protected endpoint
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        // If we get here, the middleware allowed the request, so token is valid
        setLoading(false);
      } catch (error) {
        console.error('Authentication verification failed:', error);
        
        // Only an authentication failure invalidates the session.
        if (error.response?.status === 401) {
          // Clear Redux store 
          dispatch({ type: 'LOGOUT' });
          setAuthError(true);
        }
        
        setLoading(false);
      }
    };

    verifyAuth();
  }, [token, dispatch, API_URL]);

  // Common categories (always available)
  const commonCategories = [
    { title: 'Operations Dashboard', permission: 'operationsDashboard', link: '/dashboard', icon: <DashboardOutlined /> }
  ];

  // VENTE mode categories
  const venteCategories = [
    { title: 'Devis', permission: 'CanManageQuote', link: '/devis', icon: <FileTextOutlined /> },
    { title: 'Bon de Livraison', permission: 'CanManageDeliveryNote', link: '/bonliv', icon: <FileDoneOutlined /> },
    { title: 'Préparation', permission: ['preparation', 'control', 'collection', 'deliveryNotes'], link: '/preparation', icon: <BoxPlotOutlined /> },
    { title: 'Factures', permission: 'CanManageInvoice', link: '/facture', icon: <FileExcelOutlined /> },
    { title: 'Expedition', permission: 'CanManageExpedition', link: '/exped', icon: <TruckOutlined /> },
    { title: 'Avoirs', permission: 'CanProcessReturns', link: '/avoir', icon: <RetweetOutlined /> },
    { title: 'Règlements', permission: 'CanProcessPayments', link: '/reg', icon: <DollarOutlined /> },
  ];

  // ACHAT mode categories (use achat-specific permissions)
  const achatCategories = [
    { title: 'Commande', permission: 'CanManageAchatCommande', link: '/achat-commande', icon: <FileTextOutlined /> },
    { title: 'Réception', permission: 'CanManageAchatReception', link: '/achat-reception', icon: <FileDoneOutlined /> },
    { title: 'Facture', permission: 'CanManageAchatFacture', link: '/facture-achat', icon: <FileExcelOutlined /> },
    { title: 'Avoir', permission: 'CanManageAchatAvoir', link: '/avoir-achat', icon: <RetweetOutlined /> },
  ];

  // Combine categories based on view mode
  const categories = [
   
    ...(viewMode === 'vente' ? venteCategories : achatCategories),
    ...commonCategories,
  ];

  const cardStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '150px',
  };

  const iconContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  };

  // If still loading, show spinner
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
        <span style={{ marginLeft: 12 }}>Verifying session...</span>
      </div>
    );
  }

  // If authentication error, show error and login button
  if (authError) {
    return (
      <Result
        status="403"
        title="Authentication Required"
        subTitle="Please log in to access the application."
        extra={
          <Button type="primary" onClick={() => navigate('/login')}>
            Go to Login
          </Button>
        }
      />
    );
  }

  // If authenticated, show the home page
  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ marginBottom: 0 }}>MY APPS</h2>
        {now && (
          <span style={{ color: '#888' }}>
            Server time: {(() => {
              const [datePart, timePart] = now.iso.split('T');
              const [year, month, day] = datePart.split('-');
              const [time] = timePart.split('.');
              const [hours, minutes] = time.split(':');
              return `${day}/${month}/${year} ${hours}:${minutes}`;
            })()}
          </span>
        )}
      </div>
      <Row gutter={[16, 16]} justify="center">
        {categories.map(cat => canAccess(cat.permission) && (
          <Col xs={24} sm={12} md={8} lg={6} xl={4} key={cat.title}>
            <Link to={cat.link}>
              <Card hoverable style={cardStyle}>
                <div style={iconContainerStyle}>
                  {React.cloneElement(cat.icon, { style: { fontSize: '32px' } })}
                </div>
                <Card.Meta title={cat.title} style={{ textAlign: 'center', marginTop: '12px' }} />
              </Card>
            </Link>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default Home;
